/**
 * <ec-product-card> — color swatches (switch image, link and variant for
 * Dawn's <product-form>) and a browser-local wishlist heart.
 *
 * Wishlist handles are stored in localStorage under "ec-wishlist" and a
 * "ec:wishlist:change" event is dispatched on document so a wishlist page or
 * the header can react. Use a wishlist app if lists must sync across devices.
 */
(() => {
  const STORAGE_KEY = 'ec-wishlist';

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (e) {
      return [];
    }
  };

  const writeWishlist = (handles) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      // storage unavailable (private mode): keep working for this page view
    }
    document.dispatchEvent(new CustomEvent('ec:wishlist:change', { detail: { handles } }));
  };

  if (customElements.get('ec-product-card')) return;

  class EcProductCard extends HTMLElement {
    connectedCallback() {
      // One delegated listener per card, added once: safe when the card is
      // moved, re-attached or its inner markup is re-rendered.
      if (!this.ecBound) {
        this.ecBound = true;
        this.addEventListener('click', (event) => {
          const swatch = event.target.closest('.ec-swatch');
          if (swatch && this.contains(swatch)) {
            event.preventDefault();
            this.selectSwatch(swatch);
            return;
          }
          if (event.target.closest('[data-ec-wishlist]')) this.toggleWishlist();
        });
      }

      const addLabel = this.querySelector('[data-ec-add-label]');
      if (addLabel && !this.defaultAddLabel) this.defaultAddLabel = addLabel.textContent.trim();

      if (this.querySelector('[data-ec-wishlist]')) {
        this.syncHeart();
        this.onWishlistChange = this.onWishlistChange || (() => this.syncHeart());
        document.addEventListener('ec:wishlist:change', this.onWishlistChange);
      }
    }

    disconnectedCallback() {
      if (this.onWishlistChange) document.removeEventListener('ec:wishlist:change', this.onWishlistChange);
    }

    get image() {
      const img = this.querySelector('.ec-pcard__media img.ec-pcard__img');
      return img || null;
    }

    selectSwatch(swatch) {
      this.querySelectorAll('.ec-swatch').forEach((el) => el.setAttribute('aria-pressed', String(el === swatch)));

      const { image, srcset, alt, variantId, variantUrl, available } = swatch.dataset;

      if (image) this.swapImage(image, srcset, alt);

      // product links open the chosen color (…?variant=ID)
      if (variantUrl) this.querySelectorAll('[data-ec-card-link]').forEach((link) => (link.href = variantUrl));

      // Add to cart adds the chosen variant
      const idInput = this.querySelector('input[name="id"]');
      if (variantId && idInput) {
        const isAvailable = available === 'true';
        idInput.value = variantId;
        idInput.disabled = !isAvailable;
        const addButton = this.querySelector('.ec-pcard__add');
        const addLabel = this.querySelector('[data-ec-add-label]');
        if (addButton) addButton.disabled = !isAvailable;
        if (addLabel) {
          addLabel.textContent = isAvailable
            ? this.defaultAddLabel
            : window.variantStrings?.soldOut || this.defaultAddLabel;
        }
      }
    }

    // Load the new image first, then fade it in (no flash of an empty image)
    swapImage(src, srcset, alt) {
      const img = this.image;
      if (!img || img.getAttribute('src') === src) return;

      const token = (this.swapToken = (this.swapToken || 0) + 1);
      img.classList.add('is-switching');

      const loader = new Image();
      if (img.sizes) loader.sizes = img.sizes;
      if (srcset) loader.srcset = srcset;
      loader.src = src;

      const apply = () => {
        if (token !== this.swapToken) return; // a newer swatch was clicked meanwhile
        if (srcset) img.srcset = srcset;
        img.src = src;
        if (alt) img.alt = alt;
        img.removeAttribute('loading');
        requestAnimationFrame(() => img.classList.remove('is-switching'));
      };
      (loader.decode ? loader.decode() : Promise.resolve()).then(apply, apply);
    }

    get heart() {
      return this.querySelector('[data-ec-wishlist]');
    }

    syncHeart() {
      if (!this.heart) return;
      const saved = readWishlist().includes(this.heart.dataset.ecWishlist);
      this.heart.setAttribute('aria-pressed', String(saved));
    }

    toggleWishlist() {
      if (!this.heart) return;
      const handle = this.heart.dataset.ecWishlist;
      const handles = readWishlist();
      const index = handles.indexOf(handle);
      if (index === -1) handles.push(handle);
      else handles.splice(index, 1);
      writeWishlist(handles);
    }
  }

  customElements.define('ec-product-card', EcProductCard);
})();
